<template>
  <div>
    <div class="flex items-start gap-2">
      <span v-if="!hideLabel" class="text-sm font-medium text-gray-500 mt-0.5">IDs:</span>

      <!-- Existing Identifiers -->
      <div class="flex flex-wrap items-center gap-2">
      <div
        v-for="identifier in identifiers"
        :key="identifier.id"
        class="group inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
      >
        <span class="text-xs text-gray-600">{{ identifier.identifier_type_display }}:</span>

        <!-- View/Edit Mode Toggle -->
        <div v-if="editingId !== identifier.id" class="flex items-center gap-1">
          <span class="sensitive text-xs font-medium text-gray-900">{{ identifier.identifier }}</span>
          <button
            @click="startEdit(identifier)"
            type="button" aria-label="Edit identifier" title="Edit identifier"
            class="text-gray-500 hover:text-gray-700"
          >
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
          <button
            @click="confirmDelete(identifier)"
            type="button" aria-label="Remove identifier" title="Remove identifier"
            class="text-gray-500 hover:text-red-600"
          >
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Edit Mode -->
        <div v-else class="flex items-center gap-1">
          <input
            v-model="editForm.identifier"
            @keyup.enter="confirmEdit"
            @keyup.escape="cancelEdit"
            :class="[
              'text-xs px-1 py-0 border rounded',
              editForm.error ? 'border-red-300 bg-red-50' : 'border-gray-300'
            ]"
            style="width: 100px"
            :id="`edit-input-${identifier.id}`"
          />
          <button
            @click="confirmEdit"
            :disabled="saving"
            class="text-green-600 hover:text-green-700 disabled:opacity-50"
          >
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </button>
          <button
            @click="cancelEdit"
            class="text-gray-400 hover:text-gray-600"
          >
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Add New Button/Form -->
      <div v-if="allowAdd && !adding" class="inline-flex">
        <button
          @click="startAdd"
          class="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
        >
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Add ID
        </button>
      </div>

      <!-- Add Form -->
      <div v-else-if="allowAdd && adding" class="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-md">
        <select
          v-model="addForm.identifier_type"
          class="text-xs px-1 py-0 border-0 bg-transparent focus:ring-0"
        >
          <option value="">Select type...</option>
          <option
            v-for="type in availableTypes"
            :key="type.id"
            :value="type.id"
          >
            {{ type.type_name }}
          </option>
        </select>

        <span class="text-xs text-gray-400">:</span>

        <input
          v-model="addForm.identifier"
          @keyup.enter="confirmAdd"
          @keyup.escape="cancelAdd"
          :placeholder="addForm.identifier_type ? 'Enter ID...' : ''"
          :disabled="!addForm.identifier_type"
          :class="[
            'text-xs px-1 py-0 border-0 bg-transparent focus:ring-0',
            addForm.error ? 'text-red-600' : ''
          ]"
          style="width: 100px"
          ref="addInput"
        />

        <button
          @click="confirmAdd"
          :disabled="!addForm.identifier_type || !addForm.identifier || saving"
          class="text-green-600 hover:text-green-700 disabled:opacity-50"
        >
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        </button>
        <button
          @click="cancelAdd"
          class="text-gray-400 hover:text-gray-600"
        >
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
    </div>

    <!-- Error tooltip -->
    <div v-if="errorMessage" class="absolute z-10 mt-8 px-2 py-1 text-xs text-white bg-red-500 rounded shadow-lg">
      {{ errorMessage }}
    </div>

    <!-- Confirmation Modal -->
    <ConfirmationModal
      :is-visible="showConfirmModal"
      :message="confirmMessage"
      @confirm="executeConfirmedAction"
      @cancel="cancelConfirmation"
    />
  </div>
</template>

<script>
import axios from 'axios';
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';

export default {
  name: 'EntityIdentifiers',
  emits: ['updated'],
  components: {
    ConfirmationModal
  },
  props: {
    allowAdd: { type: Boolean, default: true },
    entityId: {
      type: [String, Number],
      required: true
    },
    hideLabel: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      identifiers: [],
      identifierTypes: [],
      adding: false,
      editingId: null,
      saving: false,
      errorMessage: '',
      showConfirmModal: false,
      confirmAction: null,
      confirmMessage: '',
      addForm: {
        identifier_type: '',
        identifier: '',
        error: false
      },
      editForm: {
        identifier: '',
        original: '',
        error: false
      }
    };
  },
  computed: {
    availableTypes() {
      // Filter out types that are already used by this entity
      const usedTypeIds = this.identifiers.map(i => i.identifier_type);
      return this.identifierTypes.filter(t => !usedTypeIds.includes(t.id));
    }
  },
  async mounted() {
    // Load types first, then identifiers
    await this.fetchIdentifierTypes();
    await this.fetchIdentifiers();
  },
  watch: {
    entityId() {
      this.fetchIdentifiers();
    }
  },
  methods: {
    emitUpdated() {
      this.$emit('updated', { entityId: this.entityId, identifiers: this.identifiers.map(identifier => ({ ...identifier })) });
    },
    async fetchIdentifierTypes() {
      try {
        const response = await axios.get('/api/identifiertypes/');
        this.identifierTypes = response.data;
      } catch (error) {
        console.error('Error fetching identifier types:', error);
      }
    },

    async fetchIdentifiers() {
      try {
        const response = await axios.get('/api/identifiers/', {
          params: { entity_id: this.entityId }
        });
        // Use the identifier_type_display from the backend
        this.identifiers = response.data;
      } catch (error) {
        console.error('Error fetching identifiers:', error);
      }
    },

    getTypeDisplay(typeId) {
      const type = this.identifierTypes.find(t => t.id === typeId);
      return type ? type.type_name : 'Unknown';
    },

    startAdd() {
      this.adding = true;
      this.cancelEdit();
      this.resetAddForm();
      this.$nextTick(() => {
        if (this.availableTypes.length === 1) {
          this.addForm.identifier_type = this.availableTypes[0].id;
          if (this.$refs.addInput) {
            this.$refs.addInput.focus();
          }
        }
      });
    },

    cancelAdd() {
      this.adding = false;
      this.resetAddForm();
      this.clearError();
    },

    resetAddForm() {
      this.addForm = {
        identifier_type: '',
        identifier: '',
        error: false
      };
    },

    startEdit(identifier) {
      this.editingId = identifier.id;
      this.editForm.identifier = identifier.identifier;
      this.editForm.original = identifier.identifier;
      this.editForm.error = false;
      this.cancelAdd();
      this.$nextTick(() => {
        // Use getElementById for more reliable element access
        const element = document.getElementById(`edit-input-${identifier.id}`);
        if (element) {
          element.focus();
          element.select();
        }
      });
    },

    cancelEdit() {
      this.editingId = null;
      this.editForm = {
        identifier: '',
        original: '',
        error: false
      };
      this.clearError();
    },

    // Confirmation modal methods
    confirmAdd() {
      if (!this.addForm.identifier_type || !this.addForm.identifier) return;

      const typeName = this.getTypeDisplay(this.addForm.identifier_type);
      this.confirmMessage = `Add new identifier "${typeName}: ${this.addForm.identifier}" to this entity?`;
      this.confirmAction = this.executeAdd;
      this.showConfirmModal = true;
    },

    confirmEdit() {
      const identifier = this.identifiers.find(i => i.id === this.editingId);
      if (!identifier || this.editForm.identifier === this.editForm.original) {
        this.cancelEdit();
        return;
      }

      this.confirmMessage = `Update identifier from "${identifier.identifier_type_display}: ${this.editForm.original}" to "${identifier.identifier_type_display}: ${this.editForm.identifier}"?`;
      this.confirmAction = this.executeEdit;
      this.showConfirmModal = true;
    },

    confirmDelete(identifier) {
      this.confirmMessage = `Remove identifier "${identifier.identifier_type_display}: ${identifier.identifier}" from this entity?`;
      this.confirmAction = () => this.executeDelete(identifier.id);
      this.showConfirmModal = true;
    },

    executeConfirmedAction() {
      if (this.confirmAction) {
        this.confirmAction();
      }
      this.showConfirmModal = false;
      this.confirmAction = null;
    },

    cancelConfirmation() {
      this.showConfirmModal = false;
      this.confirmAction = null;
    },

    async executeAdd() {
      this.saving = true;
      this.clearError();

      try {
        const response = await axios.post('/api/identifiers/', {
          entity: this.entityId,
          identifier_type: this.addForm.identifier_type,
          identifier: this.addForm.identifier
        });

        const newIdentifier = response.data;
        this.identifiers.push(newIdentifier);
        this.cancelAdd();
        this.emitUpdated();
      } catch (error) {
        this.addForm.error = true;
        this.showError(error.response?.data?.identifier?.[0] || error.response?.data?.detail || 'Error adding identifier');
      } finally {
        this.saving = false;
      }
    },

    async executeEdit() {
      const identifier = this.identifiers.find(i => i.id === this.editingId);
      if (!identifier) return;

      this.saving = true;
      this.clearError();

      try {
        const response = await axios.patch(`/api/identifiers/${identifier.id}/`, {
          identifier: this.editForm.identifier
        });

        const index = this.identifiers.findIndex(i => i.id === identifier.id);
        this.identifiers[index] = response.data;
        this.cancelEdit();
        this.emitUpdated();
      } catch (error) {
        this.editForm.error = true;
        this.showError(error.response?.data?.identifier?.[0] || error.response?.data?.detail || 'Error updating identifier');
      } finally {
        this.saving = false;
      }
    },

    async executeDelete(id) {
      try {
        await axios.delete(`/api/identifiers/${id}/`);
        this.identifiers = this.identifiers.filter(i => i.id !== id);
        this.emitUpdated();
      } catch (error) {
        this.showError('Error removing identifier');
      }
    },

    showError(message) {
      this.errorMessage = message;
      setTimeout(() => {
        this.errorMessage = '';
      }, 3000);
    },

    clearError() {
      this.errorMessage = '';
    }
  }
};
</script>

<style scoped>
input:focus,
select:focus {
  outline: none;
}
</style>
